import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { GalleryPage, type DroneRecord } from '@tonist/shared';
import { PageShell } from '../components/PageShell';
import { DroneCard } from '../components/DroneCard';
import { usePreview } from '../lib/preview';
import { get, errorMessage } from '../lib/api';

type Sort = 'new' | 'plays';

export function PublicGallery() {
  const { playingId, toggle: togglePreview } = usePreview();
  const [sort, setSort] = useState<Sort>('new');
  const [items, setItems] = useState<DroneRecord[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (reset: boolean) => {
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams({ sort });
      if (!reset && cursor) q.set('cursor', cursor);
      const page = GalleryPage.parse(await get<unknown>(`/gallery?${q}`));
      setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
      setCursor(page.nextCursor);
    } catch (e) { setError(errorMessage(e)); } finally { setLoading(false); }
  };
  useEffect(() => { void load(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sort]);

  return (
    <PageShell eyebrow="Gallery" title="Shared drones" actions={
      <div className="seg" role="group" aria-label="Sort">
        <button type="button" aria-pressed={sort === 'new'} onClick={() => setSort('new')}>Newest</button>
        <button type="button" aria-pressed={sort === 'plays'} onClick={() => setSort('plays')}>Most played</button>
      </div>}>
      {error && <div className="notice notice--err">{error}</div>}
      {!error && !loading && items.length === 0 && <div className="notice">Nothing published yet. Be the first: build a drone and publish it from <Link to="/drones">your drones</Link>.</div>}
      <div className="card-grid">
        {items.map((r) => (
          <DroneCard key={r.id} drone={r.drone} preview={{ playing: playingId === r.drone.id, onToggle: () => togglePreview(r.drone) }}
            meta={<><span>{r.authorName ?? 'anonymous'}</span><span>{r.plays} plays</span></>}
            actions={<Link to={`/d/${r.slug ?? r.id}`} className="btn btn--sm btn--primary">Open</Link>} />
        ))}
      </div>
      {cursor && <div><button type="button" className="btn" disabled={loading} onClick={() => void load(false)}>{loading ? 'Loading…' : 'Load more'}</button></div>}
    </PageShell>
  );
}
